/** tm/components/ConsultResultModal.tsx — 상담 결과 입력 모달 (PeekStack 지원)
 *  2026-10-12 업무진행 설계 §8 입력 강제: 「다음 진행 *」 업무 진행 / 종결(사유 *) — 고르기 전엔 저장 안 됨
 *   업무 진행 → 막 만든 업무(단계가 모두 대기)마다 「지금 누구 차례 *」 내 차례(첫 단계 진행) / 회신 대기(☑업체 ☑FP · 기한 경과에서 빠짐)
 *   종결 → 이 상담의 업무는 중단(사유 = 「상담 종결 · 사유」)
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import type { Company, Consultation, Task, TaskTemplate, AIPreset, TaskCategory, UploadedFile, TaskDecisionItem, FP, WM, TaskStep, CompanyManager } from '../types';
import { extraManagersOf } from '../types';
import * as api from '../api';
import * as notesApi from '../api-notes';
import TaskRegisterModal from './TaskRegisterModal';
import CollectNotifyModal from './CollectNotifyModal'; // V22_COLLECT_START:22-Ca
import { C, T, R } from './ui';
import { md, stepStruck } from '../flow/flowLogic';
import { OUTLOOKS, OUTLOOK_LABEL, OUTLOOK_TONE, asOutlook } from '../dealOutlook';
import type { DealOutlook } from '../dealOutlook';
import { MODAL_HEADER, MODAL_FOOTER } from './ConsultDetailModal';
import ReportSection from './ReportSection';
import ReportMemoBar from './ReportMemoBar';
import ReportTodoEditor from './ReportTodoEditor';
import ConsultStaffTodo from './ConsultStaffTodo';
import ConsultCompanies, { type CoRef } from './ConsultCompanies';
import RenameFileList from './RenameFileList';
import ConsultRecordings from './ConsultRecordings';
import ContactFields, { type ContactValue, contactFromCompany, contactPayload } from './ContactFields';
import type { ReportTodo } from '../api-report';
import CompanyMatchFields, { type MatchFieldsValue, emptyMatchFields, matchFieldsFromCompany, otherTagsOf, mergeTags } from './CompanyMatchFields';

function d10(d: string | null | undefined): string { if (!d) return ''; return d.slice(0, 10); }

const STEP_TYPES: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  work: { label: '일반 작업', icon: '⚙️', color: '#6B7280', bg: '#F3F4F6' },
  collect: { label: '자료수집', icon: '📂', color: '#2563EB', bg: '#EFF6FF' },
  ai_questionnaire: { label: 'AI 질문지', icon: '🤖', color: '#7C3AED', bg: '#F3E8FF' },
  ai_bizplan: { label: 'AI 사업계획서', icon: '📊', color: '#7C3AED', bg: '#F3E8FF' },
  confirm: { label: '확인', icon: '✅', color: '#059669', bg: '#ECFDF5' },
  contract: { label: '계약', icon: '📝', color: '#D97706', bg: '#FFFBEB' },
  meeting: { label: '미팅', icon: '🤝', color: '#2563EB', bg: '#EFF6FF' },
  report: { label: '보고서', icon: '📋', color: '#6B7280', bg: '#F3F4F6' },
  invoice: { label: '청구', icon: '💰', color: '#D97706', bg: '#FFFBEB' },
};
function stepTypeInfo(t: string) { return STEP_TYPES[t] || STEP_TYPES.work; }
const CLOSE_REASONS = ['업체가 안 하기로 함', '해당 사업 없음', '정보 제공만', '기타'];
type Turn = { kind: 'mine' | 'wait'; who: string[] };
/* 임시 저장 (2026-10-12 지도사 「저장 안 눌러도 자동저장 내지는 임시저장 · 뒤로 갔다 오면 리셋돼」)
   적는 동안 이 브라우저에 상담별로 계속 보관 → 창을 닫았다 열어도 이어서. 「저장」에 성공하면 지운다.
   서버 상담이 임시본보다 나중에 저장됐으면(다른 기기 등) 임시본은 버린다 · 14일 지난 임시본도 버린다 */
interface ConsultDraft {
  at: number; content: string; contentCompany: string; reportTodos: ReportTodo[];
  nextAction: '' | '업무' | '종결'; closeReason: string; closeEtc: string; turns: Record<string, Turn>;
  dealOutlook?: DealOutlook | null; dealReason?: string;
  contactInfo?: { ceo: string; ceo_phone: string; ceo_email: string; manager: string; manager_phone: string; manager_email: string };
  extraMgrs?: CompanyManager[];
  real?: { on: boolean; name: string; phone: string };
  matchValue?: MatchFieldsValue;
  learned?: string;   // 내가 배운 점(2026-11-01)
}
const draftKey = (id: string) => 'roum-consult-draft:' + id;
function readDraft(c: Consultation): ConsultDraft | null {
  try {
    const d = JSON.parse(localStorage.getItem(draftKey(c.id)) || 'null') as ConsultDraft | null;
    if (!d || typeof d.at !== 'number') return null;
    const upd = Date.parse(String((c as unknown as { updated_at?: string }).updated_at || ''));
    if ((Number.isFinite(upd) && upd > d.at + 2000) || Date.now() - d.at > 14 * 86400000) { localStorage.removeItem(draftKey(c.id)); return null; }
    return d;
  } catch { return null; }
}
function hhmm(t: number): string { const d = new Date(t); return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`; }
function sortedOf(t: Task): TaskStep[] { return (t.steps || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)); }
/** 막 만든 업무 — 진행 중이고 단계가 모두 대기(아직 누구 차례인지 안 정함)
 *  · 첫 단계만 이미 「진행」인 업무도 넣는다 — 벤처인증 업무는 벤처 진행에 맞춰 첫 단계가 바로 진행이 되어
 *    「지금 누구 차례」 줄이 사라졌다(2026-10-18 지도사 「내 차례 회신 대기 버튼 없네」) · 그때는 「내 차례」가 미리 골라져 있다 */
function isFresh(t: Task): boolean { const ss = sortedOf(t); return t.status === '진행' && ss.length > 0 && (ss[0].status === '대기' || ss[0].status === '진행') && ss.slice(1).every(x => x.status === '대기'); }
function startedOf(t: Task): boolean { const f = sortedOf(t)[0]; return !!f && f.status === '진행'; }
const chip = (on: boolean): React.CSSProperties => ({ height: 32, padding: '0 12px', borderRadius: 8, border: `1.5px solid ${on ? '#2E75B6' : '#E5E7EB'}`, background: on ? '#EBF3FB' : '#fff', color: on ? '#2E75B6' : '#374151', fontSize: 12, fontWeight: on ? 700 : 500, cursor: 'pointer' });
function getCatName(id: string, cats: TaskCategory[]): string { return cats.find(c => c.id === id)?.name || ''; }
const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  '대기': { color: '#6B7280', bg: '#F3F4F6' },
  '진행': { color: '#2563EB', bg: '#EFF6FF' },
  '완료': { color: '#059669', bg: '#ECFDF5' },
};

const secH: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#1B3A5C', paddingBottom: 8, borderBottom: '2px solid #E5E7EB', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 };

interface Props {
  consultation: Consultation;
  templates: TaskTemplate[];
  taskCategories?: TaskCategory[];
  aiPresets?: AIPreset[];
  fps?: FP[];
  wms?: WM[];
  existingTasks?: Task[];
  onDone: () => void;
  onCancel: () => void;
  onToast: (msg: string) => void;
  panelMode?: boolean;
  /** 리포트 미리보기가 떠서 좌우 분할이 필요한지 상위(PeekStack)에 알린다 */
  onWideChange?: (wide: boolean) => void;
  onPushTask?: (seed?: 'gp' | 'lp') => void;
  /** PeekStack: 외부에서 업무 수정 결과를 받을 때 */
  onTaskRegDone?: (result: { content?: string; files?: UploadedFile[]; decisions: TaskDecisionItem[] }) => void;
  /** 상담한 회사(주 회사 + 관계사)가 바뀌면 — 상담 넘겨보기(ConsultPeekFlow)의 업무 등록이 같은 목록을 쓰게 (2026-10-18) */
  onCompaniesChange?: (cos: CoRef[]) => void;
  onNavigate?: (section: string, params?: unknown) => void;
}

export default function ConsultResultModal({ consultation, templates, taskCategories = [], aiPresets = [], fps = [], wms = [], existingTasks = [], onDone, onCancel, onToast, panelMode, onWideChange, onPushTask, onNavigate, onTaskRegDone: externalTaskRegDone, onCompaniesChange }: Props) {
  const [saving, setSaving] = useState(false);
  const [draft0] = useState<ConsultDraft | null>(() => readDraft(consultation));   // 처음 열 때 한 번 읽은 임시본
  const [showNavPrompt, setShowNavPrompt] = useState(false);
  // V22_COLLECT_START:22-Cb — 자료수집 시작 후 알림 모달 데이터
  const [collectNotifyData, setCollectNotifyData] = useState<{ projectId: string } | null>(null);
  const [content, setContent] = useState(draft0 ? draft0.content : (consultation.content || ''));
  // 내가 배운 점 — 나만 보는 메모 · 「현장 노트 › 배운 점」에 모여 언제든 열람 · 검색(2026-11-01 지도사) · 저장 때 같이 저장
  const [learned, setLearned] = useState<string>(draft0 && typeof draft0.learned === 'string' ? draft0.learned : '');
  const [learnLoaded, setLearnLoaded] = useState<boolean>(!!(draft0 && typeof draft0.learned === 'string'));
  useEffect(() => {
    if (learnLoaded) return;
    notesApi.getConsultLearn(consultation.id).then((r) => { if (r) setLearned(r.body || ''); }).catch(() => { /* 못 불러와도 새로 적기는 됨 */ }).finally(() => setLearnLoaded(true));
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  // 리포트 01 회사 현황 — 02(상담 내용)는 기존 content 칸을 그대로 쓴다
  const [contentCompany, setContentCompany] = useState<string>(
    draft0 ? draft0.contentCompany : (((consultation as unknown as Record<string, unknown>).content_company as string) || '')
  );
  const [reportSaved, setReportSaved] = useState<boolean>(
    consultation.status === 'completed' || consultation.status === 'sent'
  );
  // 03 해야할 일 — 리포트 전용 레이어(시스템 업무와 별개). 생성 시 그대로 넘어간다.
  const [reportTodos, setReportTodos] = useState<ReportTodo[]>(() => (draft0 && Array.isArray(draft0.reportTodos) ? draft0.reportTodos : []));
  // 리포트 미리보기 — 값이 있으면 모달을 넓혀 좌측 패널에 띄운다(2026-08-16 사용자 요청)
  const [previewUrl, setPreviewUrl] = useState('');
  // 패널 모드에서도 넓힌다. 폭은 PeekStack 이 정하므로 상위에 알려주기만 한다.
  const wide = !!previewUrl;
  useEffect(() => { if (onWideChange) onWideChange(wide); }, [wide, onWideChange]);
  const [files, setFiles] = useState<UploadedFile[]>(Array.isArray(consultation.files) ? consultation.files : []);
  const [tasks, setTasks] = useState<Task[]>(existingTasks);
  // 단계 날짜 고치기 · 단계 지우기(2026-10-29 지도사 「여기서 날짜도 수정 · 삭제도」) — 지우기는 두 번 눌러 확정(✕ → 「지우기」)
  const [dueEdit, setDueEdit] = useState<string>('');
  const [delArm, setDelArm] = useState<string>('');
  useEffect(() => { if (!delArm) return undefined; const t = window.setTimeout(() => setDelArm(''), 3000); return () => window.clearTimeout(t); }, [delArm]);
  const patchSteps = (taskId: string, fn: (steps: TaskStep[]) => TaskStep[]) => setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, steps: fn(t.steps || []) } : t)));
  async function saveDue(task: Task, step: TaskStep, v: string) {
    setDueEdit('');
    const due = v || null;
    if ((step.due ? d10(step.due) : '') === (v || '')) return;
    try { await api.updateStep(task.id, step.id, { due } as Partial<TaskStep>); patchSteps(task.id, ss => ss.map(x => (x.id === step.id ? { ...x, due: due as string } : x))); onToast(v ? `「${step.name}」 날짜를 ${md(v)}로 바꿨어요` : `「${step.name}」 날짜를 지웠어요`); }
    catch (e) { onToast(e instanceof Error ? e.message : '날짜를 저장하지 못했어요'); }
  }
  async function removeStep(task: Task, step: TaskStep) {
    if (delArm !== step.id) { setDelArm(step.id); return; }
    setDelArm('');
    try { await api.deleteStep(task.id, step.id); patchSteps(task.id, ss => ss.filter(x => x.id !== step.id)); onToast(`「${step.name}」 단계를 지웠어요`); }
    catch (e) { onToast(e instanceof Error ? e.message : '단계를 지우지 못했어요'); }
  }
  useEffect(() => { setTasks(existingTasks); }, [existingTasks]);
  // 이 상담 업무 — 관계사 업무까지(부모는 주 회사 업무만 넘긴다 · 2026-10-18)
  useEffect(() => {
    if (!consultation.id) return;
    const token = localStorage.getItem('token') || '';
    fetch(`/api/tm/tasks?consult_id=${encodeURIComponent(consultation.id)}&_=${Date.now()}`, { headers: { Authorization: 'Bearer ' + token }, cache: 'no-store' })
      .then(r => r.json()).then((j: { data?: Task[] }) => { const got = Array.isArray(j.data) ? j.data : []; if (got.length) setTasks(prev => [...prev, ...got.filter(g => !prev.some(p => p.id === g.id))]); })
      .catch(() => { /* 주 회사 업무만으로 */ });
  }, [consultation.id, existingTasks]);
  // 상담한 회사 — 주 회사 + 함께 상담한 회사(관계사)
  const [extraCos, setExtraCos] = useState<CoRef[]>([]);
  const [mainCo, setMainCo] = useState<Company | null>(null);
  const allCos = useMemo<CoRef[]>(() => [{ id: consultation.company_id, name: consultation.company_name || '' }, ...extraCos], [consultation.company_id, consultation.company_name, extraCos]);
  useEffect(() => { if (onCompaniesChange) onCompaniesChange(allCos); }, [allCos, onCompaniesChange]);
  const coName = (id: string | null | undefined) => (allCos.find(c => c.id === id) || { name: '' }).name;
  // 함께 상담한 회사의 정부지원사업 매칭 — 회사마다 따로 (2026-10-18 지도사 「함께 상담한 다른 회사도 정부지원사업 매칭 각각」)
  //   주 회사는 matchValue(아래) · 관계사는 여기 · 저장할 때 각 업체에 따로 쓴다
  interface CoMatch { v: MatchFieldsValue; other: string[]; secOff: boolean; dirty: boolean; ci: ContactValue; ciDirty: boolean }
  const [coMatch, setCoMatch] = useState<Record<string, CoMatch>>({});
  const [matchCo, setMatchCo] = useState<string>('');   // '' = 주 회사 — 고객 정보 · 매칭 칸이 함께 따른다
  /** 회사 고르기 칩 — 고객 정보 · 정부지원사업 매칭 머리에 같은 줄 */
  const coChips = () => extraCos.length > 0 ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
      {allCos.map((co, i) => {
        const id = i === 0 ? '' : co.id, on = matchCo === id, ready = i === 0 || !!coMatch[co.id];
        return (
          <button key={co.id || 'main'} type="button" disabled={!ready} onClick={() => setMatchCo(id)} title={co.name}
            style={{ maxWidth: 220, height: 30, padding: '0 12px', borderRadius: 15, border: `1px solid ${on ? C.accent : C.border}`, background: on ? '#EBF3FB' : C.white,
              color: on ? C.accent : C.text, fontSize: T.xs, fontWeight: 700, cursor: ready ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: ready ? 1 : 0.5 }}>{co.name || '주 회사'}</button>
        );
      })}
      <span style={{ fontSize: T.xs, color: C.muted }}>회사마다 따로 저장돼요</span>
    </div>
  ) : null;
  useEffect(() => {
    extraCos.forEach(co => {
      if (coMatch[co.id]) return;
      api.getCompany(co.id).then(r => {
        const comp = r.data; if (!comp) return;
        const v = matchFieldsFromCompany(comp);
        setCoMatch(prev => (prev[co.id] ? prev : { ...prev, [co.id]: { v, other: otherTagsOf(comp.tags), secOff: v.sectors_off.length > 0, dirty: false, ci: contactFromCompany(comp), ciDirty: false } }));
      }).catch(() => { /* 못 읽으면 그 회사 매칭 칸은 안 보임 */ });
    });
    if (matchCo && !extraCos.some(c => c.id === matchCo)) setMatchCo('');
  }, [extraCos]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [showTaskModal, setShowTaskModal] = useState(false);
  // 다음 진행 * — 저장된 값이 있으면 그것, 업무가 이미 있으면 업무 진행, 없으면 기본값 없이 고르게
  const [nextAction, setNextAction] = useState<'' | '업무' | '종결'>(() => draft0 && draft0.nextAction ? draft0.nextAction : (consultation.next_action === '업무' || consultation.next_action === '종결') ? consultation.next_action : (existingTasks.length ? '업무' : ''));
  const [closeReason, setCloseReason] = useState(() => { if (draft0 && draft0.closeReason) return draft0.closeReason; const r = consultation.close_reason || ''; return !r ? '' : CLOSE_REASONS.includes(r) ? r : '기타'; });
  const [closeEtc, setCloseEtc] = useState(() => { if (draft0 && draft0.closeEtc) return draft0.closeEtc; const r = consultation.close_reason || ''; return r && !CLOSE_REASONS.includes(r) ? r : ''; });
  const [turns, setTurns] = useState<Record<string, Turn>>(() => (draft0 && draft0.turns ? draft0.turns : {}));
  // 계약 가능성 · 이유(2026-10-29 지도사 「미팅 나갔다 오고 계약 가능성 있어 보이는지 · 이유」) — WM님 포털에 보임 · 안 골라도 저장됨
  const [dealOutlook, setDealOutlook] = useState<DealOutlook | null>(() => (draft0 && draft0.dealOutlook !== undefined ? asOutlook(draft0.dealOutlook) : asOutlook(consultation.deal_outlook)));
  const [dealReason, setDealReason] = useState(() => (draft0 && typeof draft0.dealReason === 'string' ? draft0.dealReason : consultation.deal_reason || ''));
  const [dealReasons, setDealReasons] = useState<string[]>([]);
  useEffect(() => { api.getDealReasons().then(r => setDealReasons(Array.isArray(r.data) ? r.data : [])).catch(() => { /* 자동완성만 없음 */ }); }, []);
  const freshTasks = tasks.filter(isFresh);
  const closing = nextAction === '종결';
  const openTasks = tasks.filter(t => t.status !== '완료' && t.status !== '중단');
  /** 빠진 칸으로 데려가기 */
  function goMissing() {
    const id = !content.trim() ? 'cr-content' : !nextAction || (nextAction === '종결') ? 'cr-next'
      : !tasks.length ? 'cr-tasks' : 'cr-task-' + ((freshTasks.find(x => { const tn = turnOf(x); return !tn || (tn.kind === 'wait' && !tn.who.length); }) || { id: '' }).id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  function setTurn(id: string, t: Partial<Turn>) { setTurns(p => ({ ...p, [id]: { kind: 'mine', who: ['업체', 'FP'], ...p[id], ...t } })); }
  /** 고른 차례 — 안 골랐어도 첫 단계가 이미 진행이면 「내 차례」 */
  function turnOf(t: Task): Turn | undefined { return turns[t.id] || (startedOf(t) ? { kind: 'mine', who: ['업체', 'FP'] } : undefined); }
  function nextMissing(): string {
    if (!content.trim()) return '02 상담 내용을 적어 주세요';
    if (!nextAction) return '다음 진행을 고르세요 · 업무 진행 / 종결';
    if (nextAction === '업무') {
      if (!tasks.length) return '진행할 업무를 추가하세요 · 업무가 없으면 「종결」';
      const t = freshTasks.find(x => !turnOf(x));
      if (t) return `「${t.type || '업무'}」 지금 누구 차례인지 고르세요`;
      const w = freshTasks.find(x => { const tn = turnOf(x); return !!tn && tn.kind === 'wait' && !tn.who.length; });
      if (w) return `「${w.type || '업무'}」 업체나 FP 중 하나는 골라 주세요`;
    }
    if (nextAction === '종결') {
      if (!closeReason) return '종결 사유를 고르세요';
      if (closeReason === '기타' && !closeEtc.trim()) return '종결 사유를 적어 주세요';
    }
    return '';
  }
  // 고객 정보(대표자·담당자 연락처) — 매칭 정보와 별개로 다룬다
  const [contactInfo, setContactInfo] = useState({ ceo: '', ceo_phone: '', ceo_email: '', manager: '', manager_phone: '', manager_email: '' });
  // 담당 직원 더 — 첫 담당자는 manager 칸 · 둘째부터 companies.extra_managers (2026-10-18 지도사 「담당직원은 여러 명 더 추가할 수 있도록」)
  const [extraMgrs, setExtraMgrs] = useState<CompanyManager[]>([]);
  // 실제 대표 — 명의 대표와 다를 때만 (183 · 2026-10-18 지도사 「명의상 대표 말고 실제 대표가 따로 있는 경우」)
  const [real, setReal] = useState<{ on: boolean; name: string; phone: string }>({ on: false, name: '', phone: '' });
  // 매칭 정보 한 벌(CompanyMatchFields) — 분류(tags)만은 상담의 gov_tags 를 우선 시드로 쓴다(아래 로더 참고)
  const [matchValue, setMatchValue] = useState<MatchFieldsValue>(() => {
    const raw = (consultation as unknown as Record<string, unknown>).gov_tags;
    const initTags: string[] = Array.isArray(raw) ? raw as string[] : (typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return []; } })() : []);
    return { ...emptyMatchFields(), tags: initTags };
  });
  // 업체 tags 중 이 화면이 다루지 않는 값(예전 자유 태그)은 저장 때 그대로 돌려놓아야 한다.
  const [otherTags, setOtherTags] = useState<string[]>([]);
  const [secOffInit, setSecOffInit] = useState(false);   // 업체가 이미 뺀 전용 대상이 있었나 (2026-10-05f)
  const [companyInfoLoaded, setCompanyInfoLoaded] = useState(false);
  const [companyLoadFail, setCompanyLoadFail] = useState(false);
  const draftRef = useRef<ConsultDraft | null>(draft0);
  const [reloadSeq, setReloadSeq] = useState(0);
  useEffect(() => {
    if (!consultation.company_id) return;
    // 업체 한 곳만 바로 — 예전엔 전체 목록에서 찾았는데, 목록이 페이지마다 섞여 못 찾으면 저장 때 업체 정보가 조용히 건너뛰어졌다(2026-10-11)
    api.getCompany(consultation.company_id).then(r => {
        const comp = r.data;
        if (comp) {
          setMainCo(comp);
          setContactInfo({
            ceo: comp.ceo || '', ceo_phone: comp.ceo_phone || '', ceo_email: comp.ceo_email || '',
            manager: comp.manager || '', manager_phone: comp.manager_phone || '', manager_email: comp.manager_email || '',
          });
          // 업체 매칭 정보로 채우되, 분류(tags)는 상담이 이미 gov_tags 를 갖고 있으면 그 값을 우선한다
          // (이 초기화가 없으면 저장 시 업체 tags 를 상담 gov_tags 로 통째 덮어써서, 업체 상세에서
          //  지정한 분류·전용분야가 조용히 사라진다).
          const fromCompany = matchFieldsFromCompany(comp);
          setMatchValue(prev => ({ ...fromCompany, tags: prev.tags.length ? prev.tags : fromCompany.tags }));
          // 임시본이 있으면 그 위에 덮는다(업체 값을 먼저 깔고 → 적던 값)
          const dr = draftRef.current;
          setExtraMgrs(extraManagersOf(comp));
          setReal({ on: !!(comp.real_ceo || comp.real_ceo_phone), name: comp.real_ceo || '', phone: comp.real_ceo_phone || '' });
          if (dr && dr.real) setReal(dr.real);
          if (dr && dr.contactInfo) setContactInfo(dr.contactInfo);
          if (dr && Array.isArray(dr.extraMgrs)) setExtraMgrs(dr.extraMgrs);
          if (dr && dr.matchValue) { const dm = dr.matchValue; setMatchValue({ ...fromCompany, ...dm }); }
          setOtherTags(otherTagsOf(comp.tags));
          setSecOffInit(fromCompany.sectors_off.length > 0);
          setCompanyInfoLoaded(true);
        } else setCompanyLoadFail(true);
      }).catch(() => setCompanyLoadFail(true));
  }, [consultation.company_id, reloadSeq]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── 임시 저장: 바뀌면 0.6초 뒤 이 브라우저에 보관 ──
  const ready = (!consultation.company_id || companyInfoLoaded || companyLoadFail) && learnLoaded;
  const snap = JSON.stringify({ content, contentCompany, reportTodos, nextAction, closeReason, closeEtc, turns, dealOutlook, dealReason, learned,
    contactInfo: companyInfoLoaded ? contactInfo : undefined, extraMgrs: companyInfoLoaded ? extraMgrs : undefined, real: companyInfoLoaded ? real : undefined, matchValue: companyInfoLoaded ? matchValue : undefined });
  const baseRef = useRef<string | null>(null);
  const [draftAt, setDraftAt] = useState<number | null>(draft0 ? draft0.at : null);
  useEffect(() => { if (ready && baseRef.current === null) baseRef.current = draftRef.current ? '' : snap; }, [ready, reloadSeq]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ready || baseRef.current === null || saving) return;
    if (snap === baseRef.current) return;                       // 연 뒤로 바뀐 것이 없음
    const t = setTimeout(() => {
      try { const at = Date.now(); localStorage.setItem(draftKey(consultation.id), JSON.stringify({ ...JSON.parse(snap), at })); setDraftAt(at); } catch { /* 저장 공간 없음 — 무시 */ }
    }, 600);
    return () => clearTimeout(t);
  }, [snap, ready, saving, consultation.id]);
  useEffect(() => { if (draft0) onToast(`적던 내용을 이어서 불러왔어요 (${hhmm(draft0.at)} 임시 저장)`); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  function clearDraft() { try { localStorage.removeItem(draftKey(consultation.id)); } catch { /* noop */ } draftRef.current = null; setDraftAt(null); }
  /** 임시본 버리기 — 마지막으로 저장한 상태로 되돌린다 */
  function discardDraft() {
    clearDraft();
    setContent(consultation.content || '');
    setContentCompany(((consultation as unknown as Record<string, unknown>).content_company as string) || '');
    setReportTodos([]);
    setNextAction((consultation.next_action === '업무' || consultation.next_action === '종결') ? consultation.next_action : (existingTasks.length ? '업무' : ''));
    const r = consultation.close_reason || '';
    setCloseReason(!r ? '' : CLOSE_REASONS.includes(r) ? r : '기타'); setCloseEtc(r && !CLOSE_REASONS.includes(r) ? r : '');
    setTurns({});
    setDealOutlook(asOutlook(consultation.deal_outlook)); setDealReason(consultation.deal_reason || '');
    baseRef.current = null; setCompanyInfoLoaded(false); setCompanyLoadFail(false); setReloadSeq(n => n + 1);   // 업체 값 다시 불러오기 → 그 상태가 새 기준
    onToast('임시본을 버리고 마지막 저장 상태로 되돌렸어요');
  }

  const initDecisions: TaskDecisionItem[] | undefined = useMemo(() => {
    if (tasks.length > 0) {
      return tasks.map(t => ({
        id: t.id,
        mode: (t.template_id ? 'template' : 'custom') as 'template' | 'custom',
        templateId: t.template_id || '',
        customName: t.type || '',
        customIcon: t.icon || '📋',
        taskStatus: (t.status === 'FP요청대기' ? 'FP요청대기' : '진행') as '진행' | 'FP요청대기',
        deadline: t.deadline ? d10(t.deadline) : '',
        category_path: (t as unknown as Record<string, unknown>).category_path as string[] || [],
        steps: (t.steps || []).map(s => ({ name: s.name, type: s.type || 'work', due: s.due ? d10(s.due) : '', extras: s.extras || {} })),
        saveAsTemplate: false,
        companyId: t.company_id,
        who: t.assigned_to || undefined,
      }));
    }
    if (Array.isArray(consultation.task_decisions) && consultation.task_decisions.length > 0) {
      return consultation.task_decisions.map(d => ({
        id: d.id || ('td_' + Math.random().toString(36).slice(2)),
        mode: (d.mode || 'custom') as 'template' | 'custom',
        templateId: d.templateId || '', customName: d.customName || '', customIcon: d.customIcon || '📋',
        taskStatus: (d.taskStatus || '진행') as '진행' | 'FP요청대기', deadline: d.deadline || '',
        // V21_HOTFIX2:extras — task_decisions 재로드 시 extras 보존
        steps: (() => { const arr = (d as unknown as Record<string, unknown>).steps || []; return (Array.isArray(arr) ? arr : []).map((s: Record<string, unknown>) => ({ name: (s.name as string) || '', type: (s.type as string) || 'work', due: (s.due as string) || '', extras: (s.extras as Record<string, unknown>) || {} })); })(),
        saveAsTemplate: false, category_path: (d as unknown as Record<string, unknown>).category_path as string[] || [],
        venture: (d as unknown as Record<string, unknown>).venture as TaskDecisionItem['venture'],
        companyId: (d as unknown as Record<string, unknown>).companyId as string | undefined,
        who: (d as unknown as Record<string, unknown>).who as string | undefined,
      }));
    }
    return undefined;
  }, [tasks, consultation.task_decisions]);

  async function handleFileUpload(fileList: FileList) {
    const fd = new FormData();
    for (let i = 0; i < fileList.length; i++) fd.append('files', fileList[i]);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/consultations/${consultation.id}/files`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd,
      });
      const json = await res.json();
      if (json.data?.files) setFiles(json.data.files);
      else if (json.success && json.data) setFiles(json.data);
    } catch { onToast('파일 업로드 실패'); }
  }

  async function handleFileDelete(idx: number) {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/consultations/${consultation.id}/files/${idx}`, {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + token },
      });
      const json = await res.json();
      if (json.data?.files) setFiles(json.data.files);
      else setFiles(prev => prev.filter((_, i) => i !== idx));
    } catch { onToast('파일 삭제 실패'); }
  }

  async function handleSave() {
    const miss = nextMissing();
    if (miss) { onToast(miss); goMissing(); return; }
    // 업체 정보를 못 불러온 채 저장하면 고객 정보 · 매칭 정보가 빠진다 — 조용히 넘기지 않고 알린다
    if (consultation.company_id && !companyInfoLoaded) {
      onToast(companyLoadFail ? '업체 정보를 불러오지 못해 저장을 멈췄어요 — 창을 닫았다가 다시 열어 주세요' : '업체 정보를 불러오는 중이에요 — 잠시 뒤 다시 저장해 주세요');
      return;
    }
    setSaving(true);
    try {
      if (companyInfoLoaded && consultation.company_id) {
        try {
          await api.updateCompany(consultation.company_id, {
            ceo: contactInfo.ceo, ceo_phone: contactInfo.ceo_phone, ceo_email: contactInfo.ceo_email,
            ceo_birth: matchValue.ceo_birth || null,
            manager: contactInfo.manager, manager_phone: contactInfo.manager_phone, manager_email: contactInfo.manager_email,
            extra_managers: extraMgrs.filter(m => m.name.trim() || m.phone.trim() || m.email.trim()),
            real_ceo: real.on ? real.name.trim() : '', real_ceo_phone: real.on ? real.phone.trim() : '',
            biz_types: matchValue.biz_types, biz_type: matchValue.biz_type,
            is_startup: !!matchValue.is_startup, company_scale: matchValue.company_scale,
            region: matchValue.region, region_detail: matchValue.region_detail,
            // 빈 문자열이면 null — Postgres 숫자/날짜 컬럼에 빈 문자열을 그대로 보내면 캐스팅 오류가 난다
            employees: matchValue.employees.trim() !== '' ? Number(matchValue.employees) : null,
            sales_eok: matchValue.sales_eok.trim() !== '' ? Number(matchValue.sales_eok) : null,
            established_date: matchValue.established_date || null,
            // 이 화면이 다루는 어휘만 교체하고 나머지는 보존 — 통째 덮어쓰면 업체 상세에서
            // 지정한 값이 사라지고, 그 업체는 이후 분류 지정 공고에서 매칭되지 않는다.
            tags: mergeTags(otherTags, matchValue),
            // 전용 대상 자동 판정 끄기 (2026-10-05f) — 원래 있었거나 새로 뺀 것이 있을 때만 보낸다(빈 값을 매번 쓰지 않게)
            ...(secOffInit || matchValue.sectors_off.length ? { sectors_off: matchValue.sectors_off } : {}),
          });
        } catch (e: unknown) {
          // 예전엔 실패를 삼켜 「저장 완료」만 떴다 — 고객 정보 · 매칭 정보가 안 들어간 걸 모르고 넘어갔다
          onToast('고객 정보 · 매칭 정보 저장 실패: ' + (e instanceof Error ? e.message : '알 수 없는 오류') + ' — 상담 내용은 아직 저장 안 됨');
          setSaving(false);
          return;
        }
      }
      // 관계사 매칭 정보 — 고친 회사만 각 업체에 (실패해도 상담 저장은 계속 · 어느 회사인지 알림)
      const coFail: string[] = [];
      for (const co of extraCos) {
        const m = coMatch[co.id];
        if (!m || (!m.dirty && !m.ciDirty)) continue;
        const v = m.v;
        try {
          await api.updateCompany(co.id, m.ciDirty && !m.dirty ? contactPayload(m.ci) : {
            ...(m.ciDirty ? contactPayload(m.ci) : {}),
            biz_types: v.biz_types, biz_type: v.biz_type, is_startup: !!v.is_startup, company_scale: v.company_scale,
            region: v.region, region_detail: v.region_detail, ceo_birth: v.ceo_birth || null,
            employees: v.employees.trim() !== '' ? Number(v.employees) : null,
            sales_eok: v.sales_eok.trim() !== '' ? Number(v.sales_eok) : null,
            established_date: v.established_date || null,
            tags: mergeTags(m.other, v),
            ...(m.secOff || v.sectors_off.length ? { sectors_off: v.sectors_off } : {}),
          });
        } catch { coFail.push(co.name); }
      }
      if (coFail.length) onToast(`${coFail.join(', ')} 고객 · 매칭 정보는 저장하지 못했어요 — 업체 상세에서 다시 넣어 주세요`);
      const reasonText = nextAction === '종결' ? (closeReason === '기타' ? closeEtc.trim() : closeReason) : '';
      await api.updateConsultation(consultation.id, {
        content, status: 'completed' as const,
        next_action: nextAction, close_reason: reasonText || null,
        deal_outlook: dealOutlook, deal_reason: dealReason.trim(),
        // 리포트 본문 — 01 은 별도 칸, 02 는 상담 내용을 그대로 쓴다
        content_company: contentCompany,
        content_consult: content,
        gov_tags: matchValue.tags,
        task_decisions: tasks.length > 0 ? tasks.map(t => ({
          id: t.id, mode: t.template_id ? 'template' : 'custom',
          templateId: t.template_id || '', customName: t.type || '',
          customIcon: t.icon || '📋', taskStatus: t.status,
          deadline: t.deadline ? d10(t.deadline) : '',
          category_path: (t as unknown as Record<string, unknown>).category_path || [],
          steps: (t.steps || []).map(s => ({ name: s.name, type: s.type || 'work', due: s.due ? d10(s.due) : '', extras: s.extras || {} })),
        })) : undefined,
      } as Record<string, unknown>);
      // 내가 배운 점 — 상담 하나에 한 줄(빈 글이면 지움) · 실패해도 상담 저장은 그대로(2026-11-01)
      try { await notesApi.saveConsultLearn(consultation.id, learned); } catch (e) { onToast('배운 점은 저장하지 못했어요 — ' + (e instanceof Error ? e.message : '다시 저장해 주세요')); }
      // 다음 진행 반영 — 업무마다 따로(하나가 안 돼도 나머지는 진행), 안 된 것은 알린다
      const fails: string[] = [];
      if (nextAction === '업무') {
        for (const t of freshTasks) {
          const first = sortedOf(t)[0];
          const turn = turnOf(t);
          if (!first || !turn) continue;
          try {
            if (turn.kind === 'wait') await api.taskFlow(t.id, { action: 'wait', stepId: first.id, wait_for: turn.who });
            else if (first.status !== '진행') await api.updateStep(t.id, first.id, { status: '진행' });
          } catch { fails.push(t.type || '업무'); }
        }
      } else if (nextAction === '종결') {
        for (const t of tasks) {
          if (t.status === '완료' || t.status === '중단') continue;
          try { await api.taskFlow(t.id, { action: 'stop', reason: `상담 종결 · ${reasonText}` }); } catch { fails.push(t.type || '업무'); }
        }
      }
      clearDraft(); baseRef.current = snap;   // 저장한 상태가 새 기준 — 저장 직후 임시본이 다시 생기지 않게
      onToast(fails.length ? `저장했어요 — 「${fails.join(', ')}」 진행 상태는 못 바꿨어요(업체별 관리에서 다시)` : '저장 완료');
      setReportSaved(true);
      // panelMode(PeekStack)에서는 아래 panelMode 분기가 panelContent만 반환하여 showNavPrompt 오버레이가
      // 렌더되지 않는다 → 프롬프트를 띄우면 onDone이 호출되지 않아 창이 멈추고 목록도 갱신 안 됨.
      // 따라서 panelMode에서는 바로 onDone()으로 닫고 부모가 목록을 새로고침하게 한다.
      if (onNavigate && !panelMode) { setShowNavPrompt(true); } else { onDone(); }
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '저장 실패'); }
    finally { setSaving(false); }
  }

  async function handleTaskRegDone(result: { content?: string; files?: UploadedFile[]; decisions: TaskDecisionItem[]; autoStartCollect?: { dId: string; si: number } }) {
    setSaving(true);
    // V22_COLLECT_START:22-Cc — autoStartCollect 매칭 결과 추적
    let autoStartTarget: { task: Task; stepIdx: number; dec: TaskDecisionItem } | null = null;
    try {
      // 이 상담의 업무만 지운다 — 업체의 다른 상담 업무까지 지우던 결함(2026-10-12 검수) 막기
      for (const t of tasks.filter(x => x.consult_id === consultation.id)) { try { await api.deleteTask(t.id); } catch { /* ignore */ } }
      const newTasks: Task[] = [];
      for (const d of result.decisions) {
        const nm = d.mode === 'template' ? templates.find(t => t.id === d.templateId)?.name || '업무' : d.customName || '업무';
        const ic = d.mode === 'template' ? templates.find(t => t.id === d.templateId)?.icon || '📋' : d.customIcon;
        const res = await api.createTask({
          assigned_to: (d as { who?: string }).who || '',   // 담당(2026-10-18) — 비면 오재인
          company_id: d.companyId || consultation.company_id, template_id: d.mode === 'template' ? d.templateId : null,
          category_path: d.category_path || [], type: nm, icon: ic,
          status: d.taskStatus as 'FP요청대기' | '진행' | '완료',
          deadline: d.deadline || null, waiting_fp: d.taskStatus === 'FP요청대기', consult_id: consultation.id, venture: api.ventureReq(d.venture),
          steps: d.steps.filter(s => s.name.trim()).map((s, i) => ({ id: '', task_id: '', name: s.name, type: s.type, status: '대기' as const, due: s.due || '', note: '', sort_order: i, extras: (s as unknown as Record<string, unknown>).extras || {} })) as unknown as [],
        });
        if (res.data) {
          const createdTask = res.data as unknown as Task;
          newTasks.push(createdTask);
          if (result.autoStartCollect && result.autoStartCollect.dId === d.id) {
            autoStartTarget = { task: createdTask, stepIdx: result.autoStartCollect.si, dec: d };
          }
        }
      }
      await api.updateConsultation(consultation.id, {
        task_decisions: result.decisions.map(d => ({
          id: d.id, mode: d.mode, templateId: d.templateId,
          customName: d.customName, customIcon: d.customIcon,
          taskStatus: d.taskStatus, deadline: d.deadline,
          category_path: d.category_path, steps: d.steps,
          venture: d.venture, companyId: d.companyId || consultation.company_id, who: d.who,
        })),
      } as Record<string, unknown>);
      try {
        // V22_HOTFIX2:cacheBust — 304 stale cache 방지 (저장 직후 fresh data 필요) · 이 상담 업무만(관계사 업무 포함 · 2026-10-18)
        const token = localStorage.getItem('token') || '';
        const res = await fetch(`/api/tm/tasks?consult_id=${encodeURIComponent(consultation.id)}&_=${Date.now()}`, { headers: { Authorization: 'Bearer ' + token }, cache: 'no-store' });
        const json = await res.json();
        setTasks(((json.data || json || []) as Task[]).filter(t => t.consult_id === consultation.id));
      } catch { setTasks(newTasks); }
      onToast(`${result.decisions.length}건 업무 저장 완료`);
      setShowTaskModal(false);
      // V22_COLLECT_START:22-Cd — 자료수집 자동 시작 처리
      if (autoStartTarget) {
        const t = autoStartTarget.task;
        const tSteps = (t as unknown as { steps?: TaskStep[] }).steps || [];
        const step = tSteps[autoStartTarget.stepIdx];
        const decStep = autoStartTarget.dec.steps[autoStartTarget.stepIdx] as unknown as { extras?: Record<string, unknown> };
        const extras = (decStep?.extras || {}) as Record<string, unknown>;
        const cfg = extras.collectConfig as { items?: Array<{ label: string; type: string; required: boolean; description?: string; section?: string; guides?: unknown[]; options?: string[] }> } | undefined;
        if (step && step.type === 'collect' && cfg?.items?.length) {
          try {
            const fpRow = (fps || []).find(f => f.id === consultation.fp_id);
            const r = await api.createCollectProjectFull({
              name: nm_from_task(t),
              company_id: t.company_id || consultation.company_id,
              fp_id: consultation.fp_id || undefined,
              items: cfg.items.map(it => ({ label: it.label, type: it.type, required: it.required, description: it.description, section: it.section || null, guides: it.guides, options: it.options })),
              source: 'task_collect',
              task_id: t.id,
              step_id: step.id,
            });
            const pid = r.data.project_id;
            await api.updateStep(t.id, step.id, {
              extras: { ...extras, collect_project_id: pid, collect_sent: true },
              status: '진행',
            } as Record<string, unknown>);
            setCollectNotifyData({ projectId: pid });
            onToast('자료수집 프로젝트가 생성되었습니다');
            void fpRow; // suppress unused (used in CollectNotifyModal props below)
          } catch (e) { onToast(e instanceof Error ? e.message : '자료수집 시작 실패'); }
        }
      }
    } catch (e: unknown) { onToast(e instanceof Error ? e.message : '업무 저장 실패'); }
    finally { setSaving(false); }
  }
  // V22_COLLECT_START:22-Cd-helper — task 이름 얻기
  function nm_from_task(t: Task): string {
    const tt = t as unknown as { type?: string; company_name?: string };
    return tt.type || tt.company_name || '자료수집';
  }

  // 벤처인증(투자) 바로 — 「🚀 벤처 GP / LP」 단추가 업무 등록을 벤처 카드 하나 넣은 채로 연다 (2026-10-18 지도사 「어디서 보라고?」)
  const [ventureSeed, setVentureSeed] = useState<'gp' | 'lp' | undefined>(undefined);
  function handleOpenTask(seed?: 'gp' | 'lp') {
    setVentureSeed(seed);
    if (panelMode && onPushTask) {
      onPushTask(seed);
    } else {
      setShowTaskModal(true);
    }
  }

  // Expose handleTaskRegDone for external (PeekStack) usage
  // This is called by ConsultPeekFlow when TaskRegisterModal completes
  if (externalTaskRegDone) {
    // The parent will call this
  }

  const headerStyle = panelMode ? { ...MODAL_HEADER, borderRadius: 0 } : MODAL_HEADER;
  const footerStyle = panelMode ? { ...MODAL_FOOTER, borderRadius: 0 } : MODAL_FOOTER;

  const panelContent = (
    <>
      {/* 헤더 — 통일 스타일 */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <button onClick={onCancel} style={{ fontSize: 22, color: '#fff', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0 }}>‹</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{consultation.company_name || '업체'}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
              {md(consultation.date)} {consultation.time || ''}{consultation.location ? ` · ${consultation.location}` : ''}
            </div>
          </div>
        </div>
      </div>

      {/* 본문 — 미리보기가 있으면 좌: PDF / 우: 입력 두 칸으로 나눈다 */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>

      {wide && (
        <div style={{ flex: '0 0 52%', minWidth: 0, borderRight: `1px solid ${C.border}`, background: C.bg, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '8px 14px', fontSize: T.xs, fontWeight: 700, color: C.sub, borderBottom: `1px solid ${C.border}`, background: C.white }}>
            📄 리포트 미리보기 <span style={{ fontWeight: 400, color: C.muted }}>— 실제 발행본입니다</span>
          </div>
          <iframe title="상담 요약 리포트 미리보기" src={previewUrl + '#toolbar=0&navpanes=0&view=FitH'}
            style={{ flex: 1, width: '100%', border: 'none', display: 'block' }} />
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '16px 20px' }}>

        {/* ══ 섹션0: 상담 메모 → 01·02·03 자동 채우기 ══ */}
        <ReportMemoBar consultId={consultation.id} onToast={onToast}
          onApplyDraft={(company, consult, todos) => {
            if (company) setContentCompany(company);
            if (consult) setContent(consult);
            if (todos && todos.length) setReportTodos(todos);
          }} />

        {/* ══ 상담한 회사 — 관계사와 함께 상담했으면 여럿(2026-10-18) ══ */}
        <div style={secH}>🏢 상담한 회사</div>
        <ConsultCompanies consultation={consultation} mainCompany={mainCo} tasks={tasks} onChange={setExtraCos} onToast={onToast} />

        {/* ══ 섹션1: 고객 정보 — 회사마다(관계사도 따로 · 2026-10-18) ══ */}
        <div style={secH}>👤 고객 정보</div>
        <div style={{ marginBottom: 24 }}>
          {coChips()}
          {matchCo && coMatch[matchCo] ? (
            <ContactFields key={matchCo} value={coMatch[matchCo].ci}
              onChange={ci => setCoMatch(prev => (prev[matchCo] ? { ...prev, [matchCo]: { ...prev[matchCo], ci, ciDirty: true } } : prev))} />
          ) : (
            <ContactFields value={{ ...contactInfo, mgrs: extraMgrs, real }}
              onChange={ci => { const { mgrs, real: rl, ...base } = ci; setContactInfo(base); setExtraMgrs(mgrs); setReal(rl); }} />
          )}
        </div>

        {/* ══ 섹션2: 상담 요약 리포트 — 01·02·03 을 품는다 ══ */}
        <div style={secH}>📄 상담 요약 리포트</div>
        <div style={{ marginBottom: 24, border: `1px solid ${C.border}`, borderRadius: R.md, padding: '14px 14px 12px', background: C.bgAlt }}>

        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.text, marginBottom: 6 }}>📋 01 회사 현황</div>
        <div style={{ marginBottom: 20 }}>
          <textarea value={contentCompany} onChange={e => setContentCompany(e.target.value)}
            style={{ width: '100%', padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: 'inherit', fontSize: T.sm, lineHeight: 1.7, outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const, minHeight: 72 }}
            placeholder={'회사에 대한 사실을 적으세요 — 업종·사업내용·매출·인력·설비 등\n한 줄에 한 항목. 하위 내용은 앞에 - 를 붙이세요.\n적을 내용이 없으면 비워두셔도 됩니다.'} />
          <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>리포트 01 섹션에 들어갑니다. 오타·띄어쓰기는 저장 후 자동으로 다듬어집니다.</div>
        </div>

        {/* 02 상담 내용 */}
        <div id="cr-content" style={{ fontSize: T.xs, fontWeight: 700, color: C.text, marginBottom: 6 }}>🤝 02 상담 내용 <span style={{ color: C.red }}>*</span></div>
        <div style={{ marginBottom: 24 }}>
          <textarea value={content} onChange={e => setContent(e.target.value)}
            style={{ width: '100%', padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: 'inherit', fontSize: T.sm, lineHeight: 1.7, outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const, minHeight: 100 }}
            placeholder={'상담에서 안내드린 내용을 적으세요\n한 줄에 한 항목. 하위 내용은 앞에 - 또는 1-1. 을 붙이세요.'} />
          {files.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {/* 파일 이름 고치기 — 이름을 누르거나 「이름 고치기」(2026-10-18 지도사 「첨부 후에도 파일명 클릭해서 수정」) */}
              <RenameFileList files={files} onRemove={i => void handleFileDelete(i)} onRename={async (i, name) => {
                const next = files.map((f, k) => (k === i ? { ...f, name } : f));
                try { await api.updateConsultation(consultation.id, { files: next }); setFiles(next); onToast('파일 이름을 고쳤어요'); }
                catch (e) { onToast(e instanceof Error ? e.message : '파일 이름을 저장하지 못했어요'); throw e; }
              }} />
            </div>
          )}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff', cursor: 'pointer', fontSize: T.sm, color: C.accent, fontWeight: 600, marginTop: 8 }}>
            📎 파일 첨부
            <input type="file" multiple onChange={e => { if (e.target.files) handleFileUpload(e.target.files); e.target.value = ''; }} style={{ display: 'none' }} />
          </label>
          {/* 상담 녹음(2026-10-28 지도사 「상담 결과 정리할 때 녹음파일 업로드 · FP포털 · WM포털에서도 보이게」) — 올리면 화자별 대화 글로 정리 · 상담 상세와 같은 부품 */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: T.xs, fontWeight: 700, color: C.text, marginBottom: 6 }}>🎙 상담 녹음</div>
            <ConsultRecordings consultId={consultation.id} onToast={onToast} />
          </div>
        </div>

        {/* 03 해야할 일 */}
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.text, marginBottom: 6 }}>📌 03 해야할 일</div>
        <div style={{ marginBottom: 16 }}>
          <ReportTodoEditor todos={reportTodos} onChange={setReportTodos} onToast={onToast} consultDate={d10(consultation.date) || ''} />
        </div>
        {/* 직원에게 일일 업무 바로 보내기 (2026-10-12 지도사 「갓길에서 결과 입력하다 직원에게 바로 배정」) */}
        <ConsultStaffTodo companyName={consultation.company_name || ''} consultDate={d10(consultation.date) || ''} reportTodos={reportTodos} onToast={onToast} />

        <ReportSection consultId={consultation.id} saved={reportSaved} onToast={onToast}
          todos={reportTodos} contentCompany={contentCompany} contentConsult={content}
          onPreview={setPreviewUrl} />

        </div>
        {/* ══ 상담 요약 리포트 끝 ══ */}

        {/* ══ 계약 가능성 (2026-10-29 지도사) — 한 번 누르고 이유 한 줄 · 다시 누르면 지움 · WM님 포털에 보임(대표님 화면 ✕) ══ */}
        <div id="cr-deal" style={{ ...secH, justifyContent: 'space-between' }}><span>🤝 계약 가능성</span><span style={{ fontSize: T.xs, fontWeight: 500, color: C.muted }}>WM님 화면에 보여요</span></div>
        <div style={{ marginBottom: 20 }}>
          <div role="radiogroup" aria-label="계약 가능성" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {OUTLOOKS.map(o => {
              const on = dealOutlook === o; const tone = OUTLOOK_TONE[o];
              return (
                <button key={o} type="button" role="radio" aria-checked={on} onClick={() => setDealOutlook(on ? null : o)}
                  style={{ ...chip(on), height: 40, fontSize: T.sm, ...(on ? { borderColor: tone.fg, background: tone.bg, color: tone.fg } : {}) }}>{OUTLOOK_LABEL[o]}</button>
              );
            })}
          </div>
          <input value={dealReason} onChange={e => setDealReason(e.target.value)} maxLength={200} list="cr-deal-reasons" aria-label="계약 가능성 이유"
            placeholder={dealOutlook ? `${OUTLOOK_LABEL[dealOutlook]}로 본 이유 한 줄` : '이유 한 줄 (예: 대표 퇴직금 준비가 필요해 보임)'}
            style={{ width: '100%', marginTop: 8, height: 36, padding: '0 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.sm, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
          <datalist id="cr-deal-reasons">{dealReasons.map(r => <option key={r} value={r} />)}</datalist>
        </div>

        {/* ══ 내가 배운 점 (2026-11-01 지도사 「상담 결과 등록할 때 내가 배운 점 따로 메모」) — 나만 봄 · 현장 노트에 모임 ══ */}
        <div id="cr-learn" style={{ ...secH, justifyContent: 'space-between' }}><span>💡 내가 배운 점</span><span style={{ fontSize: T.xs, fontWeight: 500, color: C.muted }}>나만 봐요 · 「현장 노트」에서 찾아볼 수 있어요</span></div>
        <div style={{ marginBottom: 20 }}>
          <textarea value={learned} onChange={e => setLearned(e.target.value)} maxLength={8000} aria-label="내가 배운 점"
            style={{ width: '100%', padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: 'inherit', fontSize: T.sm, lineHeight: 1.7, outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const, minHeight: 72 }}
            placeholder={'이번 상담에서 새로 알게 된 것 · 다음에 써먹을 말 · 조심할 점 (적지 않아도 저장돼요)'} />
        </div>

        {/* ══ 다음 진행 * (2026-10-12 입력 강제) ══ */}
        <div id="cr-next" style={secH}>➡️ 다음 진행 <span style={{ color: C.red }}>*</span></div>
        <div style={{ marginBottom: nextAction === '종결' ? 12 : 20 }}>
          <div role="radiogroup" aria-label="다음 진행" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(['업무', '종결'] as const).map(a => {
              const on = nextAction === a;
              return (
                <button key={a} type="button" role="radio" aria-checked={on} onClick={() => setNextAction(a)}
                  style={{ ...chip(on), height: 40, fontSize: T.sm }}>
                  {a === '업무' ? '업무 진행' : '종결 — 진행 안 함'}
                </button>
              );
            })}
          </div>
        </div>
        {nextAction === '종결' && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 6 }}>종결 사유 <span style={{ color: C.red }}>*</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
              {CLOSE_REASONS.map(r => (
                <button key={r} type="button" aria-pressed={closeReason === r} onClick={() => setCloseReason(r)} style={{ ...chip(closeReason === r), padding: '0 6px' }}>{r}</button>
              ))}
            </div>
            {closeReason === '기타' && (
              <input value={closeEtc} onChange={e => setCloseEtc(e.target.value)} placeholder="사유를 짧게" maxLength={100}
                style={{ width: '100%', marginTop: 8, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', boxSizing: 'border-box' }} />
            )}
          </div>
        )}

        {/* ══ 섹션3: 진행할 업무 ══ */}
        {/* 종결이면 같은 칸이 「중단될 업무」 — 위 선택과 아래 카드가 서로 다른 말을 하지 않게(2026-10-12 검수) */}
        <div id="cr-tasks" style={{ ...secH, justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{closing ? `📋 중단될 업무 ${openTasks.length}건` : '📋 진행할 업무'}</span>
          {!closing && (
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button onClick={() => handleOpenTask('gp')} title="대표님이 조합장(GP)으로 새 조합 — 벤처인증 진행현황에 등록"
                style={{ fontSize: T.xs, padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.purple}55`, background: '#F3E8FF', color: C.purple, cursor: 'pointer', fontWeight: 700 }}>
                🚀 벤처 GP
              </button>
              <button onClick={() => handleOpenTask('lp')} title="다른 조합에 LP(조합원)로 참여 — 조합을 고르거나 LP 후보로"
                style={{ fontSize: T.xs, padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.purple}55`, background: '#fff', color: C.purple, cursor: 'pointer', fontWeight: 700 }}>
                🚀 벤처 LP
              </button>
              <button onClick={() => handleOpenTask()}
                style={{ fontSize: T.xs, padding: '4px 12px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, cursor: 'pointer', fontWeight: 700 }}>
                + 업무 수정/추가
              </button>
            </span>
          )}
        </div>
        {closing && openTasks.length > 0 && <div style={{ marginTop: -4, marginBottom: 10, fontSize: T.xs, color: '#92400E' }}>저장하면 아래 업무가 「중단」으로 바뀌어요(사유 = 상담 종결)</div>}
        <div style={{ marginBottom: 24 }}>
          {tasks.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: C.muted, fontSize: T.xs, border: `2px dashed ${C.border}`, borderRadius: R.md }}>
              {closing ? '중단될 업무 없음' : '등록된 업무 없음 · 위 버튼으로 추가하세요'}
            </div>
          )}
          {tasks.map(task => (
            <div key={task.id} id={'cr-task-' + task.id} style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 8, opacity: closing ? 0.75 : 1 }}>
              <div style={{ padding: '10px 14px', background: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: T.md }}>{task.icon || '📋'}</span>
                {allCos.length > 1 && <span title={coName(task.company_id)} style={{ maxWidth: 140, fontSize: T.xs, padding: '2px 8px', borderRadius: 4, background: task.company_id === consultation.company_id ? '#EBF3FB' : '#F3F4F6', color: task.company_id === consultation.company_id ? C.accent : '#374151', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{coName(task.company_id) || '회사'}</span>}
                <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.type || '업무'}</span>
                {(() => { const cp = (task as unknown as Record<string, unknown>).category_path as string[] | undefined; const names = (cp || []).map(id => getCatName(id, taskCategories)).filter(Boolean); return names.length > 0 ? <span style={{ fontSize: T.xs, padding: '2px 6px', borderRadius: 4, background: '#F3F4F6', color: '#6B7280' }}>{names.join(' > ')}</span> : null; })()}
                {closing && task.status !== '완료' && task.status !== '중단'
                  ? <span style={{ fontSize: T.xs, padding: '2px 8px', borderRadius: R.sm, fontWeight: 600, background: '#F3F4F6', color: '#4B5563' }}>중단 예정</span>
                  : <span style={{ fontSize: T.xs, padding: '2px 8px', borderRadius: R.sm, fontWeight: 600,
                    background: task.status === 'FP요청대기' ? '#F3E8FF' : task.status === '완료' ? '#ECFDF5' : '#EFF6FF',
                    color: task.status === 'FP요청대기' ? '#7C3AED' : task.status === '완료' ? '#059669' : C.accent,
                  }}>{task.status}</span>}
                {task.deadline && <span style={{ fontSize: T.xs, color: C.sub }}>마감 {md(task.deadline)}</span>}
              </div>
              {nextAction === '업무' && isFresh(task) && (() => {
                const turn = turnOf(task);
                return (
                  <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', background: turn ? '#fff' : '#FFFBEB' }}>
                    <span style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, width: 92, flexShrink: 0 }}>지금 누구 차례 <span style={{ color: C.red }}>*</span></span>
                    {!turn && <span style={{ fontSize: T.xs, color: '#92400E', order: 9, marginLeft: 4 }}>골라 주세요</span>}
                    <button type="button" aria-pressed={!!turn && turn.kind === 'mine'} onClick={() => setTurn(task.id, { kind: 'mine' })} style={chip(!!turn && turn.kind === 'mine')}>내 차례</button>
                    <button type="button" aria-pressed={!!turn && turn.kind === 'wait'} onClick={() => setTurn(task.id, { kind: 'wait' })} style={chip(!!turn && turn.kind === 'wait')}>회신 대기</button>
                    {turn && turn.kind === 'wait' && ['업체', 'FP'].map(w => (
                      <label key={w} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: T.xs, color: C.text, marginLeft: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={turn.who.includes(w)} onChange={e => setTurn(task.id, { who: e.target.checked ? [...turn.who, w] : turn.who.filter(x => x !== w) })} />{w}
                      </label>
                    ))}
                  </div>
                );
              })()}
              {task.steps && task.steps.length > 0 && !closing && (
                <div style={{ padding: '6px 14px 10px' }}>
                  {task.steps.map((step: TaskStep, si: number) => {
                    const ti = stepTypeInfo(step.type);
                    // 첫 단계는 고른 「누구 차례」대로 미리 보여 준다(저장 뒤 모습)
                    const tn = isFresh(task) && step === sortedOf(task)[0] ? turnOf(task) : undefined;
                    const shown = tn ? (tn.kind === 'wait' ? '회신대기' : '진행') : step.status;
                    const sc = STATUS_COLORS[shown] || (shown === '회신대기' ? { color: '#0A7D74', bg: '#E1F8F5' } : STATUS_COLORS['대기']);
                    const isDone = shown === '완료';
                    const isNow = shown === '진행' || shown === '회신대기';
                    const dim = (!isDone && !isNow) ? 0.5 : 1;   // 흐리게는 이름 · 상태만(날짜 · 지우기 단추는 또렷하게)
                    return (
                      <div key={step.id || si} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0', borderBottom: si < task.steps.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                        <span style={{ opacity: dim, width: 18, height: 18, borderRadius: '50%', background: isDone ? '#059669' : isNow ? '#2E75B6' : '#D1D5DB', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: T.xs, fontWeight: 700, flexShrink: 0 }}>
                          {isDone ? '✓' : si + 1}
                        </span>
                        <span style={{ opacity: dim, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: T.xs, color: isDone ? C.muted : C.text, fontWeight: isNow ? 700 : 500, textDecoration: stepStruck(task, { ...step, status: shown }) ? 'line-through' : 'none' }}>{step.name}</span>
                        {/* 일반 작업은 표시 안 함(모든 줄이 같아 정보 없음) · 날짜는 같은 폭 오른쪽 맞춤 「10/16(금)」 */}
                        {step.type && step.type !== 'work' && <span style={{ opacity: dim, fontSize: T.xs, padding: '2px 6px', borderRadius: 4, background: ti.bg, color: ti.color, fontWeight: 600, flexShrink: 0 }}>{ti.label}</span>}
                        {/* 날짜 — 누르면 바로 달력(빈 날짜도 「날짜」를 눌러 넣기) */}
                        {step.id && dueEdit === step.id
                          ? <input type="date" autoFocus defaultValue={step.due ? d10(step.due) : ''} aria-label={`${step.name} 날짜`}
                              onBlur={e => void saveDue(task, step, e.target.value)} onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setDueEdit(''); }}
                              style={{ width: 128, flexShrink: 0, height: 24, fontSize: T.xs, border: `1px solid ${C.accent}`, borderRadius: 4, padding: '0 4px', fontFamily: 'inherit' }} />
                          : <button type="button" disabled={!step.id} onClick={() => setDueEdit(step.id)} title="날짜 고치기"
                              style={{ width: 72, flexShrink: 0, textAlign: 'right', fontSize: T.xs, color: step.due ? C.sub : C.muted, fontVariantNumeric: 'tabular-nums', height: 22, border: `1px solid ${C.border}`, borderRadius: 4, background: '#fff', padding: '0 6px', cursor: step.id ? 'pointer' : 'default', fontFamily: 'inherit' }}>{step.due ? md(step.due) : '날짜'}</button>}
                        <span style={{ opacity: dim, width: 52, flexShrink: 0, textAlign: 'center', fontSize: T.xs, padding: '2px 0', borderRadius: 4, background: sc.bg, color: sc.color, fontWeight: 600 }}>{shown === '회신대기' ? '회신 대기' : shown}</span>
                        {step.id && (delArm === step.id
                          ? <button type="button" onClick={() => void removeStep(task, step)} style={{ width: 48, flexShrink: 0, height: 22, border: 'none', borderRadius: 4, background: C.red, color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', padding: 0 }}>지우기</button>
                          : <button type="button" onClick={() => void removeStep(task, step)} aria-label={`${step.name} 단계 지우기`} title="단계 지우기" style={{ width: 48, flexShrink: 0, height: 22, border: 'none', background: 'none', color: C.muted, fontSize: 14, cursor: 'pointer', lineHeight: 1 }}>✕</button>)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ══ 섹션4: 정부지원사업 매칭 ══ */}
        <div style={secH}>🏛️ 정부지원사업 매칭</div>
        <div style={{ marginBottom: 12 }}>
          {coChips()}
          {matchCo && coMatch[matchCo] ? (
            <CompanyMatchFields key={matchCo} value={coMatch[matchCo].v} layout="compact" companyName={coName(matchCo)}
              onChange={patch => setCoMatch(prev => (prev[matchCo] ? { ...prev, [matchCo]: { ...prev[matchCo], v: { ...prev[matchCo].v, ...patch }, dirty: true } } : prev))} />
          ) : (
            <CompanyMatchFields value={matchValue} onChange={patch => setMatchValue(prev => ({ ...prev, ...patch }))} layout="compact" companyName={consultation.company_name || ''} />
          )}
        </div>
      </div>
      </div>

      {/* 막힌 이유 — 마우스를 올리지 않아도 늘 보이게 · 누르면 그 칸으로 */}
      {!saving && nextMissing() && (
        <button type="button" onClick={goMissing}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 20px', border: 'none', borderTop: '1px solid #FDE68A', background: '#FFFBEB', color: '#92400E', fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>
          ⚠ {nextMissing()} ›
        </button>
      )}
      {/* 하단 버튼 */}
      {draftAt && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 20px', borderTop: `1px solid ${C.border}`, background: '#F8FAFC', fontSize: T.xs, color: C.sub }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>💾 {hhmm(draftAt)} 임시 저장됨 — 이 컴퓨터에만 있어요 · 「저장」을 눌러야 업체 · 상담에 반영돼요</span>
          <button type="button" onClick={discardDraft} style={{ flexShrink: 0, border: 'none', background: 'none', color: C.sub, textDecoration: 'underline', cursor: 'pointer', fontSize: T.xs, padding: 0 }}>임시본 버리기</button>
        </div>
      )}
      <div style={footerStyle}>
        <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.md, fontWeight: 600, cursor: 'pointer' }}>취소</button>
        <button onClick={handleSave} disabled={saving}
          style={{ flex: 2, padding: '10px 0', borderRadius: 10, border: 'none', background: saving ? C.muted : 'linear-gradient(135deg, #111827, #1B3A5C)', color: '#fff', fontSize: T.md, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: nextMissing() ? 0.6 : 1 }}>
          {saving ? '⏳ 저장 중...' : nextMissing() ? '💾 저장' : nextAction === '업무' && tasks.length ? `💾 업무 ${tasks.length}건 진행하고 저장` : nextAction === '종결' ? '💾 종결하고 저장' : '💾 저장'}
        </button>
      </div>
    </>
  );

  // ═══ panelMode ═══
  if (panelMode) {
    return <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>{panelContent}</div>;
  }

  // ═══ standalone ═══
  return (<>
    {showTaskModal && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 3000 }}>
        <TaskRegisterModal
          title="업무 수정/추가"
          companyId={consultation.company_id} companyName={consultation.company_name || ''} seedVenture={ventureSeed} companies={allCos}
          fpId={consultation.fp_id || undefined}
          templates={templates} taskCategories={taskCategories} aiPresets={aiPresets}
          showConsultFields={false}
          onDone={handleTaskRegDone}
          onCancel={() => setShowTaskModal(false)}
          onToast={onToast}
          modalMaxWidth={560} modalMaxHeight="92vh"
          initialDecisions={initDecisions}
          enableAutoStartCollect={true} /* V22_COLLECT_START:22-Ce */
        />
      </div>
    )}
    {/* V22_COLLECT_START:22-Cf — 자료수집 알림 발송 모달 */}
    {collectNotifyData && (
      <CollectNotifyModal
        projectId={collectNotifyData.projectId}
        companyName={(tasks[0] as unknown as { company_name?: string })?.company_name || ''}
        ceo={real.on && real.name.trim() ? real.name.trim() : contactInfo.ceo}   /* 알림 받는 대표 = 실제 대표(있으면 · 2026-10-18) */
        ceoPhone={real.on && real.phone.trim() ? real.phone.trim() : contactInfo.ceo_phone}
        ceoEmail={contactInfo.ceo_email}
        manager={contactInfo.manager}
        managerPhone={contactInfo.manager_phone}
        managerEmail={contactInfo.manager_email}
        fpName={(fps || []).find(f => f.id === consultation.fp_id)?.name || ''}
        fpPhone={(fps || []).find(f => f.id === consultation.fp_id)?.phone || ''}
        onDone={() => { setCollectNotifyData(null); onDone(); }}
        onCancel={() => setCollectNotifyData(null)}
        onToast={onToast}
      />
    )}
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={onCancel} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.5)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', width: '100%', maxWidth: wide ? 1160 : 560, maxHeight: '92vh', height: wide ? '92vh' : undefined, background: '#fff', borderRadius: 12, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', transition: 'max-width 0.18s ease' }}>
        {panelContent}
      </div>
    </div>
    {showNavPrompt && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.55)', backdropFilter: 'blur(3px)' }} />
        <div style={{ position: 'relative', width: '100%', maxWidth: 380, background: '#fff', borderRadius: 14, padding: '24px 24px 18px', boxShadow: '0 20px 60px rgba(0,0,0,0.28)', textAlign: 'center' }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 6 }}>결과가 등록되었습니다</div>
          <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5, marginBottom: 18 }}>이 업체의 업무를 <b style={{ color: '#1B3A5C' }}>업체별 관리</b>에서 이어서 진행할 수 있습니다.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setShowNavPrompt(false); onDone(); }} style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>닫기</button>
            <button onClick={() => { setShowNavPrompt(false); onNavigate?.('companies', { companyId: consultation.company_id }); onDone(); }} style={{ flex: 2, padding: '11px 0', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #111827, #1B3A5C)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>🏢 업체 관리 열기</button>
          </div>
        </div>
      </div>
    )}
  </>);
}
